import { Fragment, useEffect, useRef } from "react";
import { Facebook } from "react-content-loader";
import YouTube from "react-youtube";

// Pull the 11-char video id out of any format stored in the database:
// standard embed urls, the legacy "youtu.com" typo domain, the "/embeded/"
// typo path, watch?v=, youtu.be, or a bare video id. Returns null when none
// found so we can fall back to a plain iframe. Mirrors the backend parser.
function getYoutubeId(url) {
    if (!url) return null;
    const raw = String(url).trim();

    // Already a bare 11-char id (e.g. "oBuTWzuZkA4")
    if (/^[A-Za-z0-9_-]{11}$/.test(raw)) return raw;

    // Match after any known marker, tolerant of typo domains/paths
    const marker = raw.match(/(?:youtu\.be\/|\/embed\/|\/embeded\/|[?&]v=|\/v\/)([A-Za-z0-9_-]{11})/);
    if (marker) return marker[1];

    // Last resort: a trailing 11-char id-looking token
    const trailing = raw.match(/([A-Za-z0-9_-]{11})(?:[?&].*)?$/);
    return trailing ? trailing[1] : null;
}

export default function VideoPlayer({ data, load, resumePosition = 0, onProgress }) {
    const videoRef = useRef(null);
    const intervalRef = useRef(null);

    // Clean up the polling interval on unmount
    useEffect(() => () => clearInterval(intervalRef.current), []);

    // Only report a real position (>= 1s) so buffering/cued events at time 0
    // never overwrite a previously saved spot.
    const report = (current, duration) => {
        if (typeof onProgress === "function" && current >= 1) {
            onProgress(current, duration || 0);
        }
    };

    // ---- YouTube (upload_type == 2) ----
    // Prefer the id parsed by the backend, fall back to client-side parsing.
    const youtubeId = data.upload_type == 2 ? (data.video_id || getYoutubeId(data.video_url)) : null;

    const onYoutubeReady = (event) => {
        if (resumePosition > 0) {
            event.target.seekTo(resumePosition, true);
        }
    };

    const onYoutubeStateChange = (event) => {
        // 1 = playing -> poll position every 5s; 2 = paused, 0 = ended -> save now.
        // Buffering/cued (3 / 5 / -1) are ignored so they can't save a 0 position.
        if (event.data === 1) {
            clearInterval(intervalRef.current);
            intervalRef.current = setInterval(() => {
                report(event.target.getCurrentTime(), event.target.getDuration());
            }, 5000);
        } else {
            clearInterval(intervalRef.current);
            if (event.data === 2 || event.data === 0) {
                report(event.target.getCurrentTime(), event.target.getDuration());
            }
        }
    };

    // ---- Native video ----
    const onLoadedMetadata = () => {
        if (videoRef.current && resumePosition > 0) {
            videoRef.current.currentTime = resumePosition;
        }
    };

    const onTimeUpdate = () => {
        const el = videoRef.current;
        if (el) report(el.currentTime, el.duration);
    };

    return (
        <Fragment>
            <div className="video-thamble text-center">
                {load && <Facebook />}

                {!load && data.upload_type == 2 ? (
                    youtubeId ? (
                        <YouTube
                            videoId={youtubeId}
                            opts={{
                                width: "100%",
                                height: "415",
                                playerVars: { start: Math.floor(resumePosition) || 0 },
                            }}
                            onReady={onYoutubeReady}
                            onStateChange={onYoutubeStateChange}
                        />
                    ) : (
                        // Non-YouTube embed: keep the original plain iframe behaviour
                        <iframe width="100%" height="415" src={data.video_url} frameBorder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
                    )
                ) : (
                    !load && (
                        <video
                            ref={videoRef}
                            width="100%"
                            height="415"
                            controls
                            src={data.video_attachment}
                            type="video/mp4"
                            onLoadedMetadata={onLoadedMetadata}
                            onTimeUpdate={onTimeUpdate}
                            onPause={onTimeUpdate}
                        ></video>
                    )
                )}
            </div>
        </Fragment>
    );
}
